import {
  Container, TextField, Button, Paper, Typography, Box,
  MenuItem, FormControl, InputLabel, Select
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';

type FormData = {
  fullName: string;
  email: string;
  username: string;
  mobile: string;
  password: string;
  confirmPassword: string;
  userType: 'User' | 'Admin';
  tuneUploadLimit: string;
  videoUploadLimit: string;
};

const SaveUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    username: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    userType: 'User',
    tuneUploadLimit: '',
    videoUploadLimit: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/users/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = res.data;
      setFormData({
        fullName: data.fullName || '',
        email: data.email || '',
        username: data.username || '',
        mobile: data.mobile || '',
        password: '',
        confirmPassword: '',
        userType: (data.userType as 'User' | 'Admin') || 'User',
        tuneUploadLimit: data.tuneUploadLimit != null ? String(data.tuneUploadLimit) : '',
        videoUploadLimit: data.videoUploadLimit != null ? String(data.videoUploadLimit) : '',
      });
    } catch (err) {
      console.error('Failed to fetch user', err);
      Swal.fire('Error', 'Failed to load user', 'error');
    }
  };

  useEffect(() => {
    if (isEdit) fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.fullName) newErrors.fullName = 'Full Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.username) newErrors.username = 'Username is required';
    if (!formData.mobile) newErrors.mobile = 'Mobile number is required';
    if (!formData.userType) newErrors.userType = 'User Type is required';

    if (!isEdit && !formData.password) newErrors.password = 'Password is required';

    if (formData.password) {
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm password';
      else if (formData.password !== formData.confirmPassword)
        newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const payload: any = { ...formData };
      delete payload.confirmPassword;

      if (isEdit && !payload.password) delete payload.password;

      payload.tuneUploadLimit = formData.tuneUploadLimit.trim() !== ''
        ? Number(formData.tuneUploadLimit)
        : null;
      payload.videoUploadLimit = formData.videoUploadLimit.trim() !== ''
        ? Number(formData.videoUploadLimit)
        : null;

      const url = `${process.env.REACT_APP_BASE_URL}/users${isEdit ? `/${id}` : ''}`;
      const method = isEdit ? 'put' : 'post';

      await axios[method](url, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      Swal.fire(isEdit ? 'Updated' : 'Created', `User ${isEdit ? 'updated' : 'created'} successfully`, 'success');
      navigate('/users');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Something went wrong';
      Swal.fire('Error', msg, 'error');
      console.error(err);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" align="center" gutterBottom>
          {isEdit ? 'Edit User' : 'Create User'}
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Full Name"
            value={formData.fullName}
            onChange={(e) => {
              setFormData({ ...formData, fullName: e.target.value });
              if (errors.fullName) setErrors({ ...errors, fullName: '' });
            }}
            error={!!errors.fullName}
            helperText={errors.fullName}
            fullWidth
          />

          <TextField
            label="Email"
            value={formData.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e.target.value });
              if (errors.email) setErrors({ ...errors, email: '' });
            }}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
          />

          <TextField
            label="Username"
            value={formData.username}
            onChange={(e) => {
              setFormData({ ...formData, username: e.target.value });
              if (errors.username) setErrors({ ...errors, username: '' });
            }}
            error={!!errors.username}
            helperText={errors.username}
            fullWidth
          />

          <TextField
            label="Mobile Number"
            value={formData.mobile}
            onChange={(e) => {
              setFormData({ ...formData, mobile: e.target.value });
              if (errors.mobile) setErrors({ ...errors, mobile: '' });
            }}
            error={!!errors.mobile}
            helperText={errors.mobile}
            fullWidth
          />

          <FormControl fullWidth error={!!errors.userType}>
            <InputLabel>User Type</InputLabel>
            <Select
              label="User Type"
              value={formData.userType}
              onChange={(e) => {
                setFormData({ ...formData, userType: e.target.value as 'User' | 'Admin' });
                if (errors.userType) setErrors({ ...errors, userType: '' });
              }}
            >
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
            </Select>
            {errors.userType && (
              <Typography variant="caption" color="error">
                {errors.userType}
              </Typography>
            )}
          </FormControl>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
              Upload Limits (leave blank for unlimited)
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                label="Max Tunes"
                type="number"
                value={formData.tuneUploadLimit}
                onChange={(e) => setFormData({ ...formData, tuneUploadLimit: e.target.value })}
                placeholder="Unlimited"
                fullWidth
                inputProps={{ min: 0 }}
                helperText="Max tunes this user can upload"
              />
              <TextField
                label="Max Videos"
                type="number"
                value={formData.videoUploadLimit}
                onChange={(e) => setFormData({ ...formData, videoUploadLimit: e.target.value })}
                placeholder="Unlimited"
                fullWidth
                inputProps={{ min: 0 }}
                helperText="Max videos this user can upload"
              />
            </Box>
          </Box>

          {/* Password (required on create, optional on edit) */}
          <TextField
            label={isEdit ? 'New Password (optional)' : 'Password'}
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => {
              setFormData({ ...formData, password: e.target.value });
              if (errors.password) setErrors({ ...errors, password: '' });
            }}
            error={!!errors.password}
            helperText={
              errors.password || (isEdit ? 'Leave blank to keep current password' : '')
            }
            fullWidth
            InputProps={{
              endAdornment: (
                <Button
                  onClick={() => setShowPassword((s) => !s)}
                  size="small"
                  tabIndex={-1}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
              ),
            }}
          />

          {/* Confirm password (only enforced if password typed) */}
          <TextField
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => {
              setFormData({ ...formData, confirmPassword: e.target.value });
              if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
            }}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            fullWidth
          />

          <Box display="flex" justifyContent="space-between">
            <Button variant="outlined" color="secondary" onClick={() => navigate('/users')}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                background: 'linear-gradient(to right, #6226A7, #F6A938)',
                color: '#fff',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(to right, #4e1f84, #db8b21)',
                },
              }}
            >
              {isEdit ? 'Update User' : 'Create User'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default SaveUser;
